import React, { useEffect, useMemo, useState } from 'react';
import StatsCards from '../components/StatsCards';
import { apiFetch } from '../utils/api';
import { ArrowRight, Download, FileText, Send } from 'lucide-react';

/** Purpose: Executes { records, permissions, on navigate }. Inputs: { records, permissions, onNavigate }. Output: Operation result. */
export default function Home({ records, permissions, onNavigate }) {
  const [homeSummary, setHomeSummary] = useState({ recentUploads: [] });
  const [summaryLoading, setSummaryLoading] = useState(false);

  const activePermissions = permissions.filter((p) => p.status === 'active');
  const recentUploadsFallback = useMemo(() => {
    return [...records]
      .sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime())
      .slice(0, 5);
  }, [records]);

  useEffect(() => {
    let isMounted = true;
    /** Purpose: Loads summary. Inputs: none. Output: Promise with operation result. */
    const loadSummary = async () => {
      setSummaryLoading(true);
      try {
        const response = await apiFetch('/records/home-summary');
        if (!response.ok) {
          throw new Error('Failed to load summary');
        }
        const data = await response.json();
        if (isMounted) {
          setHomeSummary({
            recentUploads: data?.recentUploads || []
          });
        }
      } catch (error) {
        console.error('Failed to load home summary', error);
      } finally {
        if (isMounted) setSummaryLoading(false);
      }
    };

    loadSummary();
    return () => {
      isMounted = false;
    };
  }, []);

  const recentUploads = homeSummary.recentUploads?.length > 0
    ? homeSummary.recentUploads
    : recentUploadsFallback.map((r) => ({
        id: r.id,
        title: r.title,
        recordType: r.type,
        recordDate: r.date,
        createdAt: r.uploadDate,
        provider: r.provider
      }));

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-brand-600 via-violet-600 to-brand-500 rounded-3xl p-5 sm:p-8 shadow-elevated">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
        <div className="relative">
          <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Welcome to MedCaddy</h2>
          <p className="text-white/70 mt-1.5 text-sm max-w-md">Manage, share, and secure your medical records in one place.</p>
          <div className="mt-5 flex flex-col sm:flex-row sm:flex-wrap gap-2.5 sm:gap-3">
            <button
              onClick={() => onNavigate('records')}
              className="w-full sm:w-auto px-5 py-2.5 bg-white text-brand-700 rounded-xl text-sm font-semibold hover:bg-white/90 transition-all shadow-sm flex items-center justify-center gap-2 group"
            >
              <FileText className="w-4 h-4" />
              View Records
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={() => onNavigate('import')}
              className="w-full sm:w-auto px-5 py-2.5 bg-white/15 text-white border border-white/20 rounded-xl text-sm font-medium hover:bg-white/25 transition-all flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Import
            </button>
            <button
              onClick={() => { window.location.href = '/?view=import&section=request'; }}
              className="w-full sm:w-auto px-5 py-2.5 bg-white/15 text-white border border-white/20 rounded-xl text-sm font-medium hover:bg-white/25 transition-all flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              Request
            </button>
          </div>
        </div>
      </div>

      <StatsCards records={records} activePermissions={activePermissions} />

      {/* Recent Uploads */}
      <div className="bg-white rounded-2xl border border-slate-200/60 p-4 sm:p-6 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h3 className="text-base font-semibold text-slate-900 tracking-tight">Recent Uploads</h3>
            <p className="text-xs text-slate-400 mt-0.5">Your latest medical records</p>
          </div>
          <div className="flex items-center gap-3">
            {summaryLoading && (
              <span className="text-[11px] text-slate-400 animate-pulse-soft">Loading...</span>
            )}
            <span className="text-[11px] font-medium text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full">
              {recentUploads.length} items
            </span>
          </div>
        </div>
        <div className="space-y-2">
          {recentUploads.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <FileText className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-sm text-slate-500 font-medium">No uploads yet</p>
              <p className="text-xs text-slate-400 mt-1">Import your first medical record to get started</p>
            </div>
          ) : (
            recentUploads.map((record, index) => (
              <div
                key={record.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm bg-slate-50/70 hover:bg-slate-100/70 border border-slate-100 rounded-xl p-3.5 transition-colors cursor-default"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 bg-white rounded-lg border border-slate-200/60 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 truncate text-sm">{record.title}</p>
                    <p className="text-slate-400 text-[11px]">
                      {record.recordType}{record.provider ? ` \u00b7 ${record.provider}` : ''}
                    </p>
                  </div>
                </div>
                <span className="text-[11px] text-slate-400 flex-shrink-0 sm:ml-4">
                  {record.createdAt ? new Date(record.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
